// components/MagicLink.jsx
import React, { useEffect, useState } from 'react';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { useBranding } from '../App/hooks/useBranding';

const MagicLink = () => {
  const { signIn, setActive, isLoaded } = useSignIn();
  const clerk = useClerk();
  const history = useHistory();
  const brand = useBranding();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleMagicLink = async () => {
      try {
        console.log('Clerk isLoaded:', isLoaded);
        console.log('signIn object:', signIn);
        console.log('clerk object:', clerk);

        // Wait for Clerk to be loaded
        if (!isLoaded || !signIn) {
          console.log('Clerk not ready yet, waiting...');
          setTimeout(handleMagicLink, 100);
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (!token) {
          setError('Invalid magic link - missing token');
          setStatus('error');
          return;
        }

        setStatus('processing');

        // Verify magic link and get sign-in token using fetch (not http.post since user isn't authenticated yet)
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/magic-link/verify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          },
        );

        const data = await response.json();
        console.log('Magic link verification response:', data);

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Something went wrong');
        }

        if (data.success) {
          // Not for auditors. This flag makes the app bulk-accept pending
          // declarant connection requests on arrival, which is meaningless for
          // an external auditor — and actively wrong if the same person also
          // holds an operator account, because it would accept invitations they
          // never saw. The backend echoes `kind` so we can tell the two apart.
          // Same for a verifier invite (verifier-firm-foundation phase 2): a verifier firm has no
          // declarant connection requests to accept.
          const isAuditGrant =
            data.data?.kind === 'audit_grant' || data.data?.kind === 'verifier_invite';
          if (!isAuditGrant) {
            localStorage.setItem('AutoAcceptDeclarantRequests', 'true');
          }
          if (data.data && !data.data.signInToken) {
            setStatus('success');
            // A spent verifier invite: the link is single-use (VF12) and the person now has an
            // account, so they sign in with an email code and land where the link pointed.
            const spentVerifierInvite = data.data.kind === 'verifier_invite';
            setTimeout(() => {
              window.location.href = spentVerifierInvite
                ? `/sign-in?redirect_url=${encodeURIComponent(data.data.redirectionUrl || '/verifier')}`
                : data.data.redirectionUrl || '/connections/invites';
              //   window.location.reload();
            }, 1000);
            return;
          }

          // For new users, we have a sign-in token to use
          if (data.data && data.data.signInToken) {
            try {
              // Use the sign-in token with the ticket strategy
              const signInAttempt = await signIn.create({
                strategy: 'ticket',
                ticket: data.data.signInToken,
              });

              console.log('Sign-in attempt result:', signInAttempt);

              if (signInAttempt.status === 'complete') {
                if (!isAuditGrant) {
                  localStorage.setItem('AutoAcceptDeclarantRequests', 'true');
                }
                // Set the active session
                // A verifier invite also names the firm to open; a blocked invitee gets none.
                await setActive({
                  session: signInAttempt.createdSessionId,
                  ...(data.data.organizationId ? { organization: data.data.organizationId } : {}),
                });

                setStatus('success');

                // Redirect to the specified path
                setTimeout(() => {
                  window.location.href = data.data.redirectionUrl || '/connections/invites';
                  //   window.location.reload();
                }, 1000);
              } else {
                throw new Error('Sign-in process incomplete');
              }
            } catch (clerkError) {
              console.error('Clerk sign-in error:', clerkError);
              throw new Error(
                `Authentication failed - please try again or contact support at ${brand.supportEmail}`,
              );
            }
          } else {
            throw new Error('Invalid response');
          }
        } else {
          throw new Error(data.error || data.message || 'Failed to verify magic link');
        }
      } catch (error) {
        console.error('Magic link error:', error);
        setError(error.message || 'Authentication failed');
        setStatus('error');
      }
    };

    // Only run once when component mounts
    handleMagicLink();
  }, [isLoaded, signIn, clerk, brand.supportEmail]); // Add dependencies to re-run when Clerk is ready

  const renderStatus = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryColor mx-auto mb-4"></div>
            <p>Verifying your invitation...</p>
          </div>
        );
      case 'processing':
        return (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryColor mx-auto mb-4"></div>
            <p>Setting up your account...</p>
          </div>
        );
      case 'success':
        return (
          <div className="text-center">
            <div className="text-green-600 text-4xl mb-4">✓</div>
            <p>Welcome! Redirecting to your dashboard...</p>
          </div>
        );
      case 'error':
        return (
          <div className="text-center">
            <div className="text-red-600 text-4xl mb-4">✗</div>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => history.push('/')}
              className="mt-4 px-4 py-2 bg-primaryColor text-white rounded hover:bg-opacity-90"
            >
              Go to Homepage
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-medium max-w-md w-full">{renderStatus()}</div>
    </div>
  );
};

export default MagicLink;
